import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { MediaUploader } from '@/components/MediaUploader';
import { Trash2, GripVertical, Image as ImageIcon, Video, Globe } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface GlobalScreensaverMedia {
  id: string;
  image_url: string;
  display_order: number;
  media_type: 'image' | 'video';
}

const SortableMediaItem = ({ media, onDelete }: { 
  media: GlobalScreensaverMedia; 
  onDelete: (id: string) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: media.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 md:gap-3 p-2 md:p-3 bg-white rounded-lg border shadow-sm"
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
        <GripVertical className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
      </div>
      
      {media.media_type === 'video' ? (
        <div className="relative w-14 h-14 md:w-20 md:h-20 rounded overflow-hidden bg-gray-900 flex items-center justify-center flex-shrink-0">
          <video
            src={media.image_url}
            className="w-full h-full object-cover"
            muted
            playsInline
            preload="metadata"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <Video className="w-4 h-4 md:w-6 md:h-6 text-white" />
          </div>
        </div>
      ) : (
        <img
          src={media.image_url}
          alt="Media global"
          className="w-14 h-14 md:w-20 md:h-20 object-cover rounded flex-shrink-0"
        />
      )}
      
      <div className="flex-1 flex flex-col gap-0.5 md:gap-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`px-1.5 md:px-2 py-0.5 rounded text-xs font-medium ${
            media.media_type === 'video' 
              ? 'bg-purple-100 text-purple-700' 
              : 'bg-blue-100 text-blue-700'
          }`}>
            {media.media_type === 'video' ? (
              <span className="flex items-center gap-1">
                <Video className="w-3 h-3" />
                <span className="hidden sm:inline">Video</span>
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                <span className="hidden sm:inline">Imagen</span>
              </span>
            )}
          </span>
          <span className="px-1.5 md:px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
            <Globe className="w-3 h-3 inline mr-1" />
            Global
          </span>
        </div>
        <span className="text-xs md:text-sm text-gray-600">Orden: {media.display_order + 1}</span>
      </div>
      
      <Button
        type="button"
        variant="destructive"
        size="sm"
        className="h-8 w-8 md:h-9 md:w-auto md:px-3 p-0 flex-shrink-0"
        onClick={() => onDelete(media.id)}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
};

export const GlobalScreensaverManager = () => {
  const [mediaItems, setMediaItems] = useState<GlobalScreensaverMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadMedia();
  }, []);

  const [newMediaFile, setNewMediaFile] = useState<File | null>(null);
  const [newMediaPreview, setNewMediaPreview] = useState<string | null>(null);
  const [newMediaType, setNewMediaType] = useState<'image' | 'video' | null>(null);

  useEffect(() => () => {
    if (newMediaPreview?.startsWith('blob:')) URL.revokeObjectURL(newMediaPreview);
  }, [newMediaPreview]);

  const loadMedia = async () => {
    try {
      const data = await api.globalScreensaverImages.list();
      setMediaItems((data || []).map((item: any) => ({ ...item, media_type: item.media_type || 'image' })));
    } catch (error) {
      console.error('Error loading global screensaver media:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMediaChange = (file: File | null, type: 'image' | 'video') => {
    setNewMediaFile(file);
    setNewMediaType(type);
    setNewMediaPreview(current => {
      if (current?.startsWith('blob:')) URL.revokeObjectURL(current);
      return file ? URL.createObjectURL(file) : null;
    });
  };

  const handleRemovePreview = () => {
    setNewMediaFile(null);
    setNewMediaPreview(current => {
      if (current?.startsWith('blob:')) URL.revokeObjectURL(current);
      return null;
    });
    setNewMediaType(null);
  };

  const sanitizeFileName = (name: string): string => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9.-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleMediaUpload = async () => {
    if (!newMediaFile || !newMediaType) return;

    try {
      const extension = newMediaFile.name.split('.').pop()?.toLowerCase() || '';
      const sanitizedName = sanitizeFileName(newMediaFile.name.replace(/\.[^/.]+$/, ''));
      const fileName = `global-screensaver/${Date.now()}_${sanitizedName}.${extension}`;

      const { url: publicUrl } = await api.storage.upload(newMediaFile, fileName, 'images');

      const maxOrder = mediaItems.length > 0
        ? Math.max(...mediaItems.map(item => item.display_order))
        : -1;

      await api.globalScreensaverImages.create({
        image_url: publicUrl,
        display_order: maxOrder + 1,
        media_type: newMediaType,
      });

      toast({
        title: newMediaType === 'video' ? 'Video global agregado' : 'Imagen global agregada',
        description: 'Se añadirá a todos los salvapantallas de municipios',
      });

      handleRemovePreview();
      loadMedia();
    } catch (error) {
      console.error('Error adding global media:', error);
      toast({
        title: "Error",
        description: "No se pudo agregar el archivo",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.globalScreensaverImages.delete(id);
      toast({ title: 'Elemento eliminado', description: 'El medio global fue eliminado de todos los salvapantallas' });
      loadMedia();
    } catch (error) {
      console.error('Error deleting global media:', error);
      toast({ title: 'Error', description: 'No se pudo eliminar el archivo', variant: 'destructive' });
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = mediaItems.findIndex(item => item.id === active.id);
      const newIndex = mediaItems.findIndex(item => item.id === over.id);

      const newMediaItems = arrayMove(mediaItems, oldIndex, newIndex);
      setMediaItems(newMediaItems);

      try {
        const updates = newMediaItems.map((item, index) => ({
          id: item.id,
          display_order: index
        }));

        for (const update of updates) {
          await api.globalScreensaverImages.update(update.id, { display_order: update.display_order });
        }

        toast({
          title: "Orden actualizado",
          description: "El orden de los elementos se guardó correctamente"
        });
      } catch (error) {
        console.error('Error updating order:', error);
        toast({
          title: "Error",
          description: "No se pudo actualizar el orden",
          variant: "destructive"
        });
        loadMedia();
      }
    }
  };

  const imageCount = mediaItems.filter(m => m.media_type === 'image').length;
  const videoCount = mediaItems.filter(m => m.media_type === 'video').length;

  return (
    <Card>
      <CardHeader className="p-4 md:p-6">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg md:text-xl">
            Salvapantallas Global
          </CardTitle>
        </div>
        <CardDescription>
          Los medios añadidos aquí aparecerán en TODOS los salvapantallas de municipios,
          combinándose con los medios propios de cada uno.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 md:space-y-6 p-4 md:p-6 pt-0 md:pt-0">
        {mediaItems.length < 5 ? (
          <div className="space-y-3">
            <MediaUploader
              label="Agregar imagen o vídeo global"
              preview={newMediaPreview}
              mediaType={newMediaType}
              onMediaChange={handleMediaChange}
              onRemove={handleRemovePreview}
              allowVideos
              recommendedResolution="4500×8000"
              maxImageSizeMB={30}
              maxVideoSizeMB={200}
            />
            {newMediaFile && (
              <Button 
                onClick={handleMediaUpload}
                className="w-full"
              >
                {newMediaType === 'video' ? 'Subir vídeo global' : 'Subir imagen global'}
              </Button>
            )}
          </div>
        ) : (
          <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
            Has alcanzado el máximo de 5 elementos globales.
          </p>
        )}

        {loading ? (
          <div>Cargando contenido global...</div>
        ) : mediaItems.length === 0 ? (
          <div className="text-center py-8 bg-muted/30 rounded-lg border-2 border-dashed">
            <Globe className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-gray-600 font-medium">
              No hay medios globales configurados
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Las imágenes o vídeos que subas aquí aparecerán en todos los municipios
            </p>
          </div>
        ) : (
          <div className="space-y-2 md:space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <p className="text-xs md:text-sm text-gray-600">
                Arrastra los elementos para cambiar el orden:
              </p>
              <div className="flex items-center gap-2 md:gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <ImageIcon className="w-3 h-3" />
                  {imageCount} {imageCount === 1 ? 'imagen' : 'imágenes'}
                </span>
                {videoCount > 0 && (
                  <span className="flex items-center gap-1">
                    <Video className="w-3 h-3" />
                    {videoCount} {videoCount === 1 ? 'video' : 'videos'}
                  </span>
                )}
              </div>
            </div>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={mediaItems.map(item => item.id)}
                strategy={verticalListSortingStrategy}
              >
                {mediaItems.map((media) => (
                  <SortableMediaItem
                    key={media.id}
                    media={media}
                    onDelete={handleDelete}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
